import { formatShort, formatTime } from '@/domain/dates';
import { meetingTimeFor } from '@/domain/sync';
import type { Assignment, Rider, Settings, Volunteer } from '@/shared/types';

/**
 * Prefilled messages for the volunteer and the rider.
 *
 * The app sends no messages of its own: it opens the coordinator's own texting
 * or mail app with the words already written. That keeps the reply coming back
 * to a person the volunteer recognises, and it needs no SMS provider, no extra
 * credentials, and no permission beyond the ones already granted.
 */

export interface RideParty {
  assignment: Assignment;
  volunteer: Volunteer | undefined;
  rider: Rider | undefined;
  settings: Settings;
}

/** The one-liner both sides get: when, who is driving, who is riding. */
export function rideMessage({ assignment, volunteer, rider, settings }: RideParty): string {
  const when = `${formatShort(assignment.date)} at ${formatTime(meetingTimeFor(assignment.date, settings))}`;
  const leg =
    assignment.legs === 'Both'
      ? 'the ride'
      : assignment.legs === 'Pickup only'
        ? 'the pickup'
        : 'the dropoff';

  return [
    `Hi — checking in about ${leg} on ${when}.`,
    volunteer && rider ? `${volunteer.name} is driving, ${rider.name} is riding.` : '',
    settings.meetingLocation ? `Meeting at ${settings.meetingLocation}.` : '',
    assignment.notes ? `Note: ${assignment.notes}` : '',
    'Still good?',
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * A phone number as an `sms:` link takes it. Anything that is not a digit or a
 * leading `+` is punctuation a dialler does not want.
 */
export function telHref(phone: string): string | null {
  const cleaned = phone.trim().replace(/[^\d+]/g, '');
  return cleaned.length >= 3 ? cleaned : null;
}

/**
 * One recipient per link. Multi-recipient `sms:` URLs are written differently
 * on iOS and Android and are silently dropped by some clients, so a text goes
 * to one person at a time and the group message is the email below.
 */
export function smsLink(phone: string, body: string): string | null {
  const number = telHref(phone);
  if (!number) return null;
  // `?&body=` is the form iOS accepts, and Android tolerates the extra `&`.
  return `sms:${number}?&body=${encodeURIComponent(body)}`;
}

/** Both addresses on one mail, so the volunteer and the rider see each other. */
export function mailtoLink(emails: string[], subject: string, body: string): string | null {
  const to = emails.map((email) => email.trim()).filter(Boolean);
  if (to.length === 0) return null;
  const params = new URLSearchParams({ subject, body });
  return `mailto:${to.join(',')}?${params.toString().replace(/\+/g, '%20')}`;
}
